import axios from "axios";
import { logoutSuccess, refreshTokenSuccess } from "./authSlice";
import {
  getStoredAuthSession,
  hasAuthTokens,
  normalizeAuthSession,
} from "./authSession";

const DEFAULT_TIMEOUT = 15000;
const AUTH_EXEMPT_PATHS = [
  "/all/signin",
  "/all/admin/login",
  "/all/signup",
  "/all/otp/request",
  "/all/otp/verify",
  "/all/refresh-token",
];
const AUTH_DEBUG = process.env.NODE_ENV !== "production";

let authStore = null;
let refreshPromise = null;
let defaultInterceptorsInitialized = false;
const TOKEN_EXPIRY_SKEW_MS = 15000;

const createBaseConfig = (config = {}) => ({
  baseURL: process.env.REACT_APP_BASE_URL || "",
  timeout: DEFAULT_TIMEOUT,
  headers: {
    Accept: "application/json",
    ...(config.headers || {}),
  },
  ...config,
});

const refreshClient = axios.create(
  createBaseConfig({
    headers: {
      "Content-Type": "application/json",
    },
  })
);

const getCurrentSession = () => {
  const sessionFromStore = authStore?.getState?.()?.auth?.user;
  return normalizeAuthSession(sessionFromStore) || getStoredAuthSession();
};

const parseTokenPayload = (token) => {
  if (!token || typeof token !== "string") {
    return null;
  }

  try {
    const [, payload] = token.split(".");

    if (!payload) {
      return null;
    }

    if (typeof window === "undefined" || typeof window.atob !== "function") {
      return null;
    }

    const normalizedBase64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decodedPayload = window.atob(normalizedBase64);

    return JSON.parse(decodedPayload);
  } catch {
    return null;
  }
};

const isAccessTokenExpired = (accessToken) => {
  const payload = parseTokenPayload(accessToken);
  const expiresAt = Number(payload?.exp);

  if (!expiresAt) {
    return false;
  }

  return expiresAt * 1000 <= Date.now() + TOKEN_EXPIRY_SKEW_MS;
};

const redirectToAuthentication = () => {
  if (typeof window === "undefined") {
    return;
  }

  const currentPath = window.location.pathname || "";
  const targetPath =
    currentPath.startsWith("/admin") ||
    currentPath.startsWith("/subscription-plans") ||
    currentPath.startsWith("/coupons") ||
    currentPath.startsWith("/service") ||
    currentPath.startsWith("/user") ||
    currentPath.startsWith("/partners")
      ? "/admin-login"
      : "/login";

  if (currentPath !== targetPath) {
    window.location.replace(targetPath);
  }
};

const performSafeLogout = () => {
  refreshPromise = null;
  authStore?.dispatch?.(logoutSuccess());
  redirectToAuthentication();
};

const isAuthExemptRequest = (config) => {
  const requestUrl = `${config?.baseURL || ""}${config?.url || ""}`;

  return AUTH_EXEMPT_PATHS.some((path) => requestUrl.includes(path));
};

const applyAccessToken = (config, accessToken) => {
  const nextConfig = {
    ...config,
    headers: {
      ...(config?.headers || {}),
    },
  };

  if (accessToken) {
    nextConfig.headers.Authorization = `Bearer ${accessToken}`;
  } else {
    delete nextConfig.headers.Authorization;
  }

  return nextConfig;
};

const refreshAccessToken = async () => {
  const session = getCurrentSession();

  if (!hasAuthTokens(session)) {
    throw new Error("Your session has expired. Please log in again.");
  }

  if (!refreshPromise) {
    if (AUTH_DEBUG) {
      console.info("[auth] refreshing access token");
    }

    refreshPromise = refreshClient
      .post("/all/refresh-token", {
        refreshToken: session.refreshToken,
      })
      .then((response) => {
        const refreshedSession = normalizeAuthSession({
          ...session,
          ...(response?.data || {}),
        });

        if (!refreshedSession?.accessToken) {
          throw new Error("Refresh token response did not include an access token.");
        }

        if (AUTH_DEBUG) {
          console.info("[auth] refresh succeeded");
        }

        authStore?.dispatch?.(
          refreshTokenSuccess({
            accessToken: refreshedSession.accessToken,
            refreshToken:
              refreshedSession.refreshToken || session.refreshToken,
          })
        );

        return refreshedSession.accessToken;
      })
      .catch((error) => {
        if (AUTH_DEBUG) {
          console.error("[auth] refresh failed", error?.response?.status || error?.message);
        }

        performSafeLogout();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const getValidAccessToken = async () => {
  const session = getCurrentSession();
  const token = session?.accessToken || session?.token || session?.refreshToken;

  if (!token) {
    return "";
  }

  if (!isAccessTokenExpired(token)) {
    return token;
  }

  if (session?.refreshToken && session.refreshToken !== token) {
    try {
      return await refreshAccessToken();
    } catch {
      return token;
    }
  }

  return token;
};

const attachAuthInterceptors = (instance) => {
  if (instance.__authInterceptorsAttached) {
    return instance;
  }

  instance.interceptors.request.use(
    async (config) => {
      if (config?.skipAuthRefresh || config?.skipAuth || isAuthExemptRequest(config)) {
        return config;
      }

      const accessToken = await getValidAccessToken();

      if (AUTH_DEBUG) {
        console.info("[auth] request", config?.method?.toUpperCase(), config?.url);
      }

      return applyAccessToken(config, accessToken);
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      const responseStatus = error?.response?.status;

      if (
        !originalRequest ||
        originalRequest.skipAuthRefresh ||
        originalRequest._retry ||
        isAuthExemptRequest(originalRequest) ||
        ![401, 403].includes(responseStatus)
      ) {
        return Promise.reject(error);
      }

      try {
        originalRequest._retry = true;
        if (AUTH_DEBUG) {
          console.warn(
            "[auth] retrying after auth error",
            responseStatus,
            originalRequest?.url
          );
        }
        const accessToken = await refreshAccessToken();
        return instance(applyAccessToken(originalRequest, accessToken));
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
  );

  Object.defineProperty(instance, "__authInterceptorsAttached", {
    value: true,
    enumerable: false,
    configurable: false,
    writable: false,
  });

  return instance;
};

export const initializeAuthHttp = (store) => {
  authStore = store;

  if (!defaultInterceptorsInitialized) {
    attachAuthInterceptors(axios);
    defaultInterceptorsInitialized = true;
  }
};

export const createAuthHttpClient = (config = {}) =>
  attachAuthInterceptors(axios.create(createBaseConfig(config)));
